/**
 * Bytebeat URL Fragment Serialization
 *
 * Fragment format (before base64url encoding):
 *   name|bitrate|code[*arg1*arg2*...]
 *
 * - name: display name of the bytebeat
 * - bitrate: samples per second (e.g. 8000, 11025, 22050, 44100)
 * - code: the bytebeat expression (uses `t` as the time variable)
 * - args: optional variable arguments separated by "*"
 *
 * The whole string is base64url encoded and used as the URL hash.
 */

export interface BytebeatData {
  name: string;
  bitrate: number;
  code: string;
  args: Record<string, number>;
}

export interface BytebeatFragment {
  name: string;
  bitrate: number;
  code: string;
  /** Raw arg values from URL, in order */
  argValues: number[];
}

const SEPARATOR = '|';
const ARG_SEPARATOR = '*';

function toBase64Url(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(b64: string): string {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (padded.length % 4)) % 4;
  const padded2 = padded + '='.repeat(padding);
  const binary = atob(padded2);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

export function serializeFragment(data: BytebeatFragment): string {
  const argStr = data.argValues.length > 0
    ? ARG_SEPARATOR + data.argValues.join(ARG_SEPARATOR)
    : '';
  const raw = `${data.name}${SEPARATOR}${data.bitrate}${SEPARATOR}${data.code}${argStr}`;
  return toBase64Url(raw);
}

export function deserializeFragment(hash: string): BytebeatFragment | null {
  try {
    const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!stripped) return null;
    const raw = fromBase64Url(stripped);
    const firstPipe = raw.indexOf(SEPARATOR);
    if (firstPipe === -1) return null;
    const secondPipe = raw.indexOf(SEPARATOR, firstPipe + 1);
    if (secondPipe === -1) return null;

    const name = raw.slice(0, firstPipe);
    const bitrateStr = raw.slice(firstPipe + 1, secondPipe);
    const rest = raw.slice(secondPipe + 1);

    const bitrate = parseInt(bitrateStr, 10);
    if (isNaN(bitrate)) return null;

    // Split code from args by first "*"
    const starIdx = rest.indexOf(ARG_SEPARATOR);
    let code: string;
    let argValues: number[] = [];

    if (starIdx === -1) {
      code = rest;
    } else {
      code = rest.slice(0, starIdx);
      const argParts = rest.slice(starIdx + 1).split(ARG_SEPARATOR);
      argValues = argParts.map(Number).filter((n) => !isNaN(n));
    }

    return { name, bitrate, code, argValues };
  } catch {
    return null;
  }
}

/**
 * Detect free variables (non-t identifiers) in the bytebeat expression.
 * Returns an array of unique variable names found that are not 't'.
 */
export function detectVariables(code: string): string[] {
  // Match identifiers that are not standard JS keywords or 't'
  const reserved = new Set([
    't', 'Math', 'sin', 'cos', 'tan', 'abs', 'floor', 'ceil', 'round',
    'sqrt', 'pow', 'log', 'min', 'max', 'PI', 'E', 'random',
    'parseInt', 'parseFloat', 'isNaN', 'isFinite', 'Infinity', 'NaN',
    'true', 'false', 'undefined', 'null',
    'var', 'let', 'const', 'function', 'return', 'if', 'else', 'for',
    'while', 'do', 'break', 'continue', 'new', 'delete', 'typeof', 'instanceof',
    'void', 'in', 'of', 'switch', 'case', 'default', 'throw', 'try', 'catch', 'finally',
  ]);

  const identifierRegex = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g;
  const found = new Set<string>();
  let match: RegExpExecArray | null;

  while ((match = identifierRegex.exec(code)) !== null) {
    const name = match[1];
    if (!reserved.has(name) && !/^\d/.test(name)) {
      found.add(name);
    }
  }

  return Array.from(found).sort();
}

/**
 * Build the args object by mapping detected variable names to provided values.
 */
export function buildArgs(variables: string[], argValues: number[]): Record<string, number> {
  const args: Record<string, number> = {};
  variables.forEach((v, i) => {
    if (i < argValues.length) {
      args[v] = argValues[i];
    }
  });
  return args;
}
